import type { ReactNode } from 'react';
import { useParams, useLocation, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CasePageHeader } from '@/components/layout/CasePageHeader';
import { casePageMeta } from '@/config/casePageMeta';
import { requireCaseId } from '@/lib/routeParams';

/**
 * CaseLayout Component
 * 
 * Wrapper for all /cases/:caseId/* routes.
 * Enforces uniform header structure across all case pages.
 * 
 * ROUTING CONTRACT ENFORCEMENT:
 * - This component appears EXACTLY ONCE in router tree (at /cases/:caseId parent)
 * - All case-scoped routes are children (rendered via <Outlet />)
 * - Runtime assertion verifies header count = 1 on every navigation
 * 
 * Rules:
 * - All /cases/:caseId/* routes MUST be children of this layout
 * - No case route may bypass it or duplicate it
 * - Footer stays global (AppLayout)
 * - Sidebar stays global (AppLayout)
 * 
 * Header derives from route segment (NOT from page component).
 * 
 * OUTLET CONTEXT:
 * Child routes can access { caseId, setHeaderActions } to dynamically set header actions.
 */

interface CaseLayoutProps {
  /**
   * Optional page-specific actions (e.g., "Upload Exhibit", "New Entry")
   * Rendered in the header's action slot.
   * NOTE: For child routes, pass via outlet context or route state
   */
  actions?: ReactNode;
  /**
   * Optional override for title (if not using route-based lookup)
   * NOTE: For child routes, pass via outlet context or route state
   */
  title?: string;
  /**
   * Optional override for subtitle
   * NOTE: For child routes, pass via outlet context or route state
   */
  subtitle?: string;
}

/**
 * Outlet context provided to child routes
 */
export interface CaseLayoutOutletContext {
  caseId: string;
  setHeaderActions: (actions: ReactNode) => void;
}

export function CaseLayout({ actions, title, subtitle }: CaseLayoutProps) {
  const { caseId } = useParams<{ caseId: string }>();
  requireCaseId(caseId);
  const location = useLocation();
  const [headerActions, setHeaderActions] = useState<ReactNode>(null);
  
  // Extract the route segment after /cases/:id/
  // e.g., /cases/abc123/journal → "journal"
  // e.g., /cases/abc123 → ""
  const pathParts = location.pathname.split('/');
  const caseIndex = pathParts.indexOf('cases');
  const routeSegment = pathParts[caseIndex + 2] || '';
  
  // Support sub-routes like filings/builder
  const subSegment = pathParts[caseIndex + 3];
  const fullSegment = subSegment ? `${routeSegment}/${subSegment}` : routeSegment;
  
  // Lookup metadata from single source of truth
  const meta = casePageMeta[fullSegment] || casePageMeta[routeSegment] || { title: 'Case' };
  
  // Allow explicit overrides
  const finalTitle = title || meta.title;
  const finalSubtitle = subtitle !== undefined ? subtitle : meta.subtitle;
  
  // Dev-mode assertion: detect header violations
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Check for exactly one CasePageHeader
      const headers = document.querySelectorAll('[data-testid="case-page-header"]');
      if (headers.length !== 1) {
        console.error(
          `[LAYOUT VIOLATION] Expected exactly 1 CasePageHeader, found ${headers.length}`,
          Array.from(headers)
        );
      }

      // Check for exactly one CaseLayout instance
      const layouts = document.querySelectorAll('[data-component="CaseLayout"]');
      if (layouts.length !== 1) {
        console.error(
          `[LAYOUT VIOLATION] Expected exactly 1 CaseLayout, found ${layouts.length}`,
          Array.from(layouts)
        );
      }

      // Check for rogue <h1> elements (page-level headers not owned by CasePageHeader)
      const rogueTitles = document.querySelectorAll(
        'main h1:not([data-owned-by="CasePageHeader"])'
      );
      if (rogueTitles.length > 0) {
        console.error(
          `[LAYOUT VIOLATION] Found ${rogueTitles.length} rogue page-level <h1> elements. ` +
          `Only CasePageHeader may render <h1>. Check page components for rogue headers.`,
          Array.from(rogueTitles)
        );
      }
    }
  }, [location.pathname]);
  
  return (
    <div className="flex flex-col h-full" data-component="CaseLayout">
      <CasePageHeader 
        title={finalTitle}
        subtitle={finalSubtitle}
        actions={headerActions || actions}
      />
      <div className="flex-1 overflow-auto">
        {/* Render child route components via Outlet */}
        <Outlet context={{ caseId, setHeaderActions }} />
      </div>
    </div>
  );
}
